import type { CSSProperties } from "react";

/** Official Google multicolor "G" logo per branding guidelines. */
export function GoogleLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.0 24.0 0 0 0 0 21.56l7.98-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
      <path fill="none" d="M0 0h48v48H0z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Google-branded button (per Google Sign-In Branding Guidelines)
// ---------------------------------------------------------------------------

type GoogleBrandButtonProps =
  | { href: string; onClick?: never; label?: string }
  | { href?: never; onClick: () => void; label?: string };

/**
 * Branded "Continue with Google" button following Google Sign-In guidelines.
 * White background, #747775 border, Roboto Medium 14px, Google "G" logo.
 * Renders as <a> when href is provided, <button> otherwise.
 */
export function GoogleBrandButton(props: GoogleBrandButtonProps) {
  const { label = "Continue with Google" } = props;

  const content = (
    <>
      <span style={iconWrapperStyle}>
        <GoogleLogo />
      </span>
      <span style={textStyle}>{label}</span>
    </>
  );

  if ("href" in props && props.href) {
    return (
      <a href={props.href} style={buttonStyle}>
        {content}
      </a>
    );
  }

  return (
    <button type="button" onClick={props.onClick} style={buttonStyle}>
      {content}
    </button>
  );
}

/**
 * Google branding: light theme button.
 * Fill: #FFFFFF, Stroke: #747775 1px inside, Font: Roboto Medium 14px, Color: #1F1F1F
 */
const buttonStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid #747775",
  borderRadius: 20,
  padding: 0,
  background: "#FFFFFF",
  cursor: "pointer",
  height: 44,
  position: "relative",
  overflow: "hidden",
  textDecoration: "none",
};

/** White background container for the Google "G" icon, per branding guidelines. */
const iconWrapperStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 20,
  height: 20,
  marginLeft: 12,
  flexShrink: 0,
};

/** Button text per Google branding: Roboto Medium 14px, color #1F1F1F. */
const textStyle: CSSProperties = {
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 500,
  fontSize: 14,
  lineHeight: "20px",
  color: "#1F1F1F",
  paddingLeft: 10,
  paddingRight: 12,
};

// ---------------------------------------------------------------------------
// Scope disclosure for Google OAuth consent
// ---------------------------------------------------------------------------

interface ScopeGroup {
  scopes: string[];
  label: string;
  description: string;
}

const SCOPE_GROUPS: ScopeGroup[] = [
  {
    scopes: ["gmail.modify"],
    label: "Gmail",
    description:
      "Read and organize your emails so YAGNI can triage your inbox, draft replies, and surface important messages. YAGNI will never delete your emails.",
  },
  {
    scopes: ["calendar.readonly", "calendar.events"],
    label: "Google Calendar",
    description:
      "View your upcoming events and create, update, or cancel meetings on your behalf for meeting prep and schedule management.",
  },
  {
    scopes: ["contacts.readonly"],
    label: "Google Contacts",
    description:
      "View your contacts to identify people in your emails and calendar events.",
  },
  {
    scopes: ["drive.readonly", "drive.file"],
    label: "Google Drive",
    description:
      "Browse files in your Drive and create new files to help reference and organize your documents.",
  },
  {
    scopes: ["spreadsheets"],
    label: "Google Sheets",
    description:
      "Create and edit spreadsheets to build reports and trackers on your behalf.",
  },
  {
    scopes: ["documents"],
    label: "Google Docs",
    description:
      "Create and edit documents to draft and maintain written content on your behalf.",
  },
  {
    scopes: ["presentations"],
    label: "Google Slides",
    description:
      "Create and edit presentations to help build slide decks on your behalf.",
  },
];

/**
 * Consumer-friendly disclosure of which Google permissions are being requested.
 * Pass the same comma-separated scope string used in the OAuth URL.
 */
export function GoogleScopeDisclosure({ scopeString, defaultOpen = false }: { scopeString: string; defaultOpen?: boolean }) {
  const requestedScopes = scopeString.split(",").map((s) => s.trim());
  const relevantGroups = SCOPE_GROUPS.filter((group) =>
    group.scopes.some((scope) => requestedScopes.includes(scope)),
  );

  if (relevantGroups.length === 0) return null;

  return (
    <details open={defaultOpen} className="text-xs mt-3 rounded-lg border border-[var(--print-border)] bg-[var(--print-bg)] shadow-[var(--shadow-print)] p-3 text-left">
      <summary className="font-medium text-[var(--text-secondary)] cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
        YAGNI will request access to {relevantGroups.map((g) => g.label).join(", ")}
      </summary>
      <ul className="space-y-1.5 list-none p-0 m-0 mt-2">
        {relevantGroups.map((group) => (
          <li key={group.label} className="leading-relaxed">
            <span className="font-medium text-[var(--text-primary)]">
              {group.label}
            </span>
            <span className="text-[var(--text-tertiary)]">
              {" "}&mdash; {group.description}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}
